import Text from '@ingka/text';
import '@ingka/grid/style.scss';
import { useGetLotteryTemplatesQuery } from '@lotta/self-service/lottery-fe/data-access-state';
import Loading, { LoadingBall } from '@ingka/loading';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import type { LocaleParams } from '@lotta/self-service/lottery-fe/types-router';
import { PageIndicator } from '@lotta/self-service/lottery-fe/ui-common';
import { useContext } from 'react';
import { FeatureFlagContext } from '@lotta/self-service/lottery-fe/util-feature-flags';
import InlineMessage from '@ingka/inline-message';
import { LotteryTemplate } from './components/lottery-template/LotteryTemplate';
import styles from './LotteryTemplates.module.scss';

/* eslint-disable-next-line */
export type LotteryTemplatesProps = {};

export const LotteryTemplates: React.FC<LotteryTemplatesProps> = () => {
  const featureFlags = useContext(FeatureFlagContext);
  const { countryCode } = useParams<LocaleParams>() as LocaleParams;
  const { data, isLoading, error } = useGetLotteryTemplatesQuery(countryCode);
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Loading text={`${t('general-fetching-template')}`}>
        <LoadingBall />
      </Loading>
    );
  }

  if (error || !data) {
    return <Text headingSize="m">{t('general-something-went-wrong')}</Text>;
  }

  let { lotteryTemplates } = data;

  if (!Array.isArray(lotteryTemplates)) {
    return <Text headingSize="m">{t('general-template-not-available')}</Text>;
  }

  if (!featureFlags.swipe) {
    lotteryTemplates = lotteryTemplates.filter((template) => template.id !== 'swipe');
  }

  if (!featureFlags.purchase) {
    lotteryTemplates = lotteryTemplates.filter((template) => template.id !== 'monthly-purchase');
  }

  if (!featureFlags.simpleSignup) {
    lotteryTemplates = lotteryTemplates.filter((template) => template.id !== 'simple-signup');
  }

  if (!featureFlags.purchaseAndSimpleSignup) {
    lotteryTemplates = lotteryTemplates.filter(
      (template) => template.id !== 'purchase-and-simple-signup'
    );
  }

  if (!lotteryTemplates.length) {
    return <Text headingSize="m">{t('general-template-not-available')}</Text>;
  }

  const templateTranslationMap: { [key: string]: string } = {
    'monthly-purchase-title': t('template-purchase-contest-title'),
    'monthly-purchase-description': t('template-purchase-contest-description'),
    'simple-signup-title': t('template-simple-signup-contest-title'),
    'simple-signup-description': t('template-simple-signup-contest-description'),
    'swipe-title': t('template-swipe-contest-title'),
    'swipe-description': t('template-swipe-contest-description'),
    'purchase-and-simple-signup-title': t('template-purchase-and-simple-signup-contest-title'),
    'purchase-and-simple-signup-description': t(
      'template-purchase-and-simple-signup-contest-description'
    ),
  };

  return (
    <div className={styles['container']} data-testid="templates-container">
      <Text className={styles['header']}>{t('general-create-lottery')}</Text>
      <PageIndicator pages={8} currentPage={1} text={t('create-lottery-template-choice')} />
      <InlineMessage
        className={styles['inline-message']}
        variant="informative"
        title={`${t('form-explanation-message-templates-title')}`}
        body={`${t('form-explanation-message-templates-body')}`}
      />
      <div className={styles['cards-container']}>
        {lotteryTemplates.map((template) => {
          template = {
            ...template,
            title: templateTranslationMap[template.id + '-title'],
            description: templateTranslationMap[template.id + '-description'],
          };
          return <LotteryTemplate lotteryTemplate={template} key={template.id} />;
        })}
      </div>
    </div>
  );
};
